import { DISMISS, useNotification } from "collections";
import {
  DESKTOP_DEVICE_FORM_SETTINGS,
  POLY_LENS_DESKTOP,
  LENS_DESKTOP
} from "Constants";
import { LoadingIndicator } from "elements";
import { DeviceFragment, useCapabilitiesQuery } from "generated/graphql";
import { Redirect, Route, Switch } from "polyrouter";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setNavItem } from "store/settingsNavItems";
import { useQuery, maskTypename } from "urql";
import { useTranslation } from "utilities/i18n";
import { DESKTOP_DEVICE_SETTINGS_NAV } from "../Settings-Shared/DeviceSettingsComponents";
import DesktopDeviceSettings from "./DesktopDeviceSettings";
import { useGetInitialValues, useDeviceGroupPolicyQuery } from "./hooks";
import { BasicDesktopDeviceSettings, C10Data } from "./schemas/types";
import { getC10Value, generateC10String } from "./utilities/c10";
import { DesktopInheritanceLoader } from "routes/Manage/Policy/DesktopInheritanceLoader";
import { resetDesktopDeviceSettingsInheritanceAndNav } from "store/desktopDeviceSettings";
import { DesktopDevicePolicyScopeProvider } from "./ProvisionedDesktopDeviceSettingsRouter";

const DesktopDeviceSettingsRouter: React.FC<{
  device: DeviceFragment;
}> = ({ device }) => {
  const { t } = useTranslation();
  const [
    deviceSettings,
    setDeviceSettings
  ] = useState<BasicDesktopDeviceSettings | null>(null);
  const dispatch = useDispatch();
  const onUpdate = (settings: BasicDesktopDeviceSettings) => {
    setDeviceSettings(settings);
  };
  const redirect = useRef<boolean>(false);
  const settings = useRef<BasicDesktopDeviceSettings | null>(null);
  const [addNotification] = useNotification();

  useEffect(() => {
    return () => {
      dispatch(resetDesktopDeviceSettingsInheritanceAndNav());
    };
  }, [dispatch]);

  const { data: c10List, fetching: fetchingC10List } = useCapabilitiesQuery({
    variables: {
      deviceId: device.id
    }
  });
  const capabilityCategories = [
    "video",
    "wireless",
    "general",
    "call_control",
    "audio",
    "com",
    "poly"
  ];
  const query: Partial<C10Data> = {};
  const capabiltyValue = `{value, constraints{options}}`;

  const createQueryObject = (
    path: string[],
    obj: Record<any, any>,
    value: string
  ) => {
    let key = path[0];
    if (path.length === 1) {
      obj[key] = value;
    } else {
      if (capabilityCategories.includes(key)) {
        if (!obj[key]) {
          obj[key] = {};
        }
        createQueryObject(path.slice(1), obj[key], value);
      }
    }
  };
  // c10List.capabilities will always include 'com.poly.network_information.interfaces'
  // so we need to make sure the array is < 2 since it will always have 1 element
  const isC10ListEmpty =
    c10List?.capabilities && c10List.capabilities.length < 2;

  if (!isC10ListEmpty) {
    c10List?.capabilities.forEach(capability => {
      createQueryObject(capability.id.split("."), query, capabiltyValue);
    });
  }

  const queryString = JSON.stringify(query);
  const graphQLqueryString =
    Object.keys(query).length === 0
      ? `{com{poly{audio{bass${capabiltyValue}}}}}`
      : queryString.replace(/['":]+/g, "");

  const c10Query = `
  query capabilitiesQuery {
    capabilitiesQuery(deviceId: "${device.id}")
      ${graphQLqueryString}
  }`;

  const [{ data: c10Data }] = useQuery<{ capabilitiesQuery: C10Data }, object>({
    query: c10Query,
    pause: fetchingC10List,
    requestPolicy: "network-only"
  });

  const { devicePolicy, executeQuery: executor } = useDeviceGroupPolicyQuery(
    isC10ListEmpty,
    c10List?.capabilities,
    {
      id: device.id,
      productId: device.product?.id ?? "",
      name: device.name
    }
  );
  const devicePolicySource = (
    devicePolicy?.devicePolicyCapabilities.sources || []
  ).find(source => source.type === "device");

  const initialValues = useGetInitialValues(
    maskTypename(c10Data?.capabilitiesQuery)
  );

  useEffect(() => {
    if (!settings.current) {
      settings.current = initialValues;
      setDeviceSettings(settings.current);
    }
  }, [initialValues]);

  const categoryWithSettings = useMemo<Array<string>>(() => {
    const categories: string[] = [];
    if (deviceSettings) {
      Object.entries(DESKTOP_DEVICE_FORM_SETTINGS).forEach(
        ([category, categorySettings]) => {
          const settingsWithValues = categorySettings.filter(setting => {
            const values = getC10Value(
              deviceSettings,
              generateC10String(setting.accessor, false).split(".")
            );
            return values !== undefined && values.value !== undefined;
          });
          if (settingsWithValues.length > 0) {
            categories.push(category);
          }
        }
      );
    }
    return categories;
  }, [deviceSettings]);

  useEffect(() => {
    categoryWithSettings.forEach(category => {
      dispatch(setNavItem(DESKTOP_DEVICE_SETTINGS_NAV[category]));
    });
  }, [categoryWithSettings, dispatch]);

  useEffect(() => {
    if (
      !device?.connected ||
      !device?.connections ||
      !device?.connections.some(
        connection =>
          connection.name === POLY_LENS_DESKTOP ||
          connection.name === LENS_DESKTOP
      ) ||
      isC10ListEmpty
    ) {
      if (redirect.current === true) {
        addNotification({
          type: "info",
          message: t("DEVICE_SETTINGS_UNAVAILABLE"),
          dismiss: DISMISS.DEFAULT,
          persistThroughNav: true
        });
      }
      redirect.current = true;
    }

    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c10List, device, isC10ListEmpty]);

  if (redirect.current === true) {
    return <Redirect to={`/manage/inventory/details/${device.id}`} />;
  }

  if (categoryWithSettings.length < 1) {
    return <LoadingIndicator />;
  }

  const redirectPath =
    DESKTOP_DEVICE_SETTINGS_NAV[categoryWithSettings[0]]?.url.replace(
      /^\//,
      ""
    ) || "general";

  return (
    <DesktopDevicePolicyScopeProvider>
      <Switch>
        <Route nest path="/:category">
          <DesktopDeviceSettings
            deviceSettings={deviceSettings}
            onUpdate={onUpdate}
            deviceGroupPolicy={devicePolicySource}
            refreshData={executor}
          />
        </Route>
        <Route nest path="/">
          <Redirect
            persist={["DeviceFilterState", "InsightsFilterState"]}
            nest
            to={redirectPath}
          />
        </Route>
      </Switch>
      <DesktopInheritanceLoader
        settings={devicePolicy?.devicePolicyCapabilities.capabilities}
        sources={devicePolicy?.devicePolicyCapabilities.sources}
        device={{
          id: device.id,
          name: device.name,
          productId: device.product?.id || ""
        }}
        hardwareModel={{
          id: device.model?.id || "",
          name: device.hardwareModel || ""
        }}
      />
    </DesktopDevicePolicyScopeProvider>
  );
};

export default DesktopDeviceSettingsRouter;
